<script setup lang="ts">import { toRef as _toRef, computed as _computed } from 'vue';

import {UserStatusType} from '@toolify/server/src/models/mongoose/UserModel/enum/UserStatusType'
import EntityStatusCircleComponent
  from '@toolify/client/src/modules/entities/components/EntityStatusCircleComponent.vue'
import ImageComponent from '@toolify/client/src/modules/ui/components/ImageComponent.vue'
import {
  IFileVariantResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IFileVariantResponseInstance'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import LogoIconComponent from '@toolify/client/src/modules/layout/components/LogoIconComponent.vue'
import {ColorVariantType} from '@toolify/client/src/modules/ui/enum/ColorVariantType'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'

import {IResponseEntity} from '@toolify/server/src/services/EntityService/types/IResponseEntity'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {
  IUserResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'
import {
  IAppEntityResponsePayload,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IAppEntityResponsePayload'
import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {useParticularColors} from '@toolify/client/src/modules/color/composables/useParticularColors'
import {computed} from 'vue'
import {getEntityInitialsByEntity} from '../functions/getEntityInitialsByEntity'
import {getParticularColorByEntity} from '@toolify/client/src/modules/entities/functions/getParticularColorByEntity'
import {getGuestDisplayInfo} from '../functions/getGuestDisplayInfo'

interface Props {
  alt?: string
  statusType?: UserStatusType
  isEditable?: boolean
  entity: IResponseEntity
}
const props = withDefaults(defineProps<Props>(), {
  alt: '',
  statusType: null,
  isEditable: false,
})

interface Emits {
  (e: 'editAvatar'): void
}
const emit = defineEmits<Emits>()

const __$temp_1 = (useThemeVariables()),
  getThemeVariable = _toRef(__$temp_1, 'getThemeVariable');
const __$temp_2 = (useParticularColors()),
  getColorByParticularColor = _toRef(__$temp_2, 'getColorByParticularColor');


const userBackgroundWrapperStyle = _computed(() => {
  if(props.entity?.entityType === EntityType.App) {
    const iconColor = (props.entity.payload as IAppEntityResponsePayload)?.iconColor
    if(iconColor) {
      return {
        background: iconColor,
      }
    }
  }
  const particularColor = getParticularColorByEntity(props.entity)
  if(!particularColor) {
    return {}
  }
  return {
    background: getColorByParticularColor.value(particularColor),
  }
})

const isAgentEntity = _computed(() => {
  return props.entity?.entityType === EntityType.System
})

const agentFileVariants = _computed<IFileVariantResponseInstance[] | null>(() => {
  if(props.entity?.entityType !== EntityType.Agent) {
    return null
  }
  const agentPayload = props.entity.payload as {
    completedAvatarFileVariants?: IFileVariantResponseInstance[]
    completedIconFileVariants?: IFileVariantResponseInstance[]
  } | null
  return agentPayload?.completedAvatarFileVariants?.length
    ? agentPayload.completedAvatarFileVariants
    : agentPayload?.completedIconFileVariants ?? null
})

const fallbackIconClass = _computed<IconNameType | null>(() => {
  if(props.entity?.entityType === EntityType.User) {
    return null
  }
  if(props.entity?.entityType === EntityType.Bot) {
    return IconNameType.Robot
  }
  if(props.entity?.entityType === EntityType.Guest) {
    return getGuestDisplayInfo(props.entity.entityId).icon
  }
  if(props.entity?.entityType === EntityType.Agent) {
    return agentFileVariants.value?.length ? null : IconNameType.Robot
  }
  return null
})

const fileVariants = _computed<IFileVariantResponseInstance[]>(() => {
  if(props.entity?.entityType === EntityType.User) {
    return (props.entity.payload as IUserResponseInstance)?.completedAvatarFileVariants
  }
  if(props.entity?.entityType === EntityType.App) {
    return (props.entity.payload as IAppEntityResponsePayload)?.completedIconFileVariants
  }
  if(props.entity?.entityType === EntityType.Agent) {
    return agentFileVariants.value
  }
  return null
})

const userInitials = computed(() => {
  return getEntityInitialsByEntity(props.entity)
})
</script>

<template>
  <div class="user-avatar">
    <ImageComponent
      :fileVariants="fileVariants"
      :alt="alt"
      class="image"
      :isEditable="isEditable"
      @editAvatar="emit('editAvatar')"
    >
      <template #error>
        <div class="user-background-wrapper" :style="userBackgroundWrapperStyle">
          <LogoIconComponent
            v-if="isAgentEntity"
            :color="ColorVariantType.Neutral"
            class="entity-logo"
          />
          <IconComponent
            v-else-if="fallbackIconClass"
            :name="fallbackIconClass"
            class="entity-icon"
            :weightType="IconWeightType.Solid"
          />
          <span v-else class="initials-text">
            {{userInitials}}
          </span>
        </div>
      </template>
    </ImageComponent>
    <div class="status" v-if="statusType && statusType !== UserStatusType.Offline">
      <EntityStatusCircleComponent
        :statusType="statusType"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  @use '../../../styles/variables' as variables;

  .status {
    width: 55%;
    height: 55%;
    border-radius: 50%;
    position: absolute;
    left: 60%;
    top: 60%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: map-get(variables.$spacing, space-1);
    box-sizing: border-box;
    background: map-get(variables.$colors, container-low)
  }

  .user-background-wrapper {
    &.type-user {
      background: map-get(variables.$colors, primary);
    }

    &.type-bot {

    }

    &.type-system {

    }
    container-type: inline-size; //better performance then only size
    background: map-get(variables.$colors, primary);
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .entity-icon{
    width: 50% !important;
    height: 50% !important;
    color: map-get(variables.$colors, on-primary) !important;

    :deep(.icon-content) {
      width: 100%;
      height: 100%;
    }
  }

  :deep(.entity-logo) {
    --logo-size: 62cqw !important;
  }

.initials-text {
  font-size: map-get(variables.$textSizes, text-sm);
  font-size: 45cqw;
  font-weight: 600;
  text-transform: uppercase;
  text-align: center;
  color: map-get(variables.$colors, on-primary) !important;
  line-height: 1;
}

  .user-avatar {
    height: map-get(variables.$sizes, size-md);
    aspect-ratio: 1 / 1;
    border-radius: 50%;
    background: map-get(variables.$colors, container-lowest);
    position: relative;


    &:deep(.image) {
      width: 100%;
      height: 100%;
      border-radius: 50%;
    }

    &:deep(.image img) {
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      object-fit: cover;
    }
  }
</style>
